import { MutationObserver, type QueryClient } from "@tanstack/react-query";

import { showToast } from "@/components/ui/sonner";
import { isQuotaError } from "@/lib/api/quota";
import { downloadFile } from "@/lib/browser/download";

import {
  attachExportJob,
  beginExportJob,
  cancelExportJobs,
  dropExportJob,
  exportJobsStore,
  isPollingExportJob,
  runExportReadyHandlers,
  setExportJobNotice,
  type BeginExportJobInput,
  type ExportJobRefusal,
  type ExportJobSettlement,
} from "./store";
import {
  showExportFailedToast,
  showExportLostToast,
  showExportPreparingToast,
  showExportReadyToast,
  showExportRefusedToast,
  showExportStartErrorToast,
  showExportTimedOutToast,
} from "./toasts";
import type {
  ExportJobAdapter,
  PollingExportJob,
  TrackedExportJob,
} from "./types";

export interface StartExportJobOptions extends BeginExportJobInput {
  /** The entry point's own request; the tracker runs it and tracks the id. */
  post: () => Promise<{ job_id: string }>;
  /**
   * Sees a failed `post`, never a refusal or a quota 429 (the tracker and the
   * global handler report those). Return true when the caller showed its own
   * copy (402, 409, a re-price) to suppress the default "Export failed".
   */
  onStartError?: (error: unknown) => boolean;
}

export type StartExportJobResult =
  | { ok: true; key: string; jobId: string }
  | { ok: false; error: unknown };

export class ExportJobRefusedError extends Error {
  constructor(public reason: ExportJobRefusal) {
    super(
      reason === "busy"
        ? "An export for this scope is already in progress"
        : "Too many exports in progress",
    );
    this.name = "ExportJobRefusedError";
  }
}

export async function startExportJob(
  queryClient: QueryClient,
  { post, onStartError, ...input }: StartExportJobOptions,
): Promise<StartExportJobResult> {
  const begun = beginExportJob(input, Date.now());
  if (!begun.ok) {
    showExportRefusedToast(begun.reason, input);
    return { ok: false, error: new ExportJobRefusedError(begun.reason) };
  }

  const { job } = begun;
  const { key } = job;

  // A cache-level mutation: quota / 403 / no-org handling still runs, the
  // generic 5xx toast doesn't, and it settles even if the caller unmounts.
  const observer = new MutationObserver(queryClient, {
    mutationFn: post,
    meta: { suppressGlobalErrorToast: true },
  });
  let jobId: string;
  try {
    ({ job_id: jobId } = await observer.mutate());
  } catch (error) {
    if (!dropExportJob(key)) return { ok: false, error };
    if (!isQuotaError(error) && !onStartError?.(error)) {
      showExportStartErrorToast(error);
    }
    return { ok: false, error };
  } finally {
    // Detached, the finished mutation is garbage-collected after gcTime.
    observer.reset();
  }

  invalidateAdapterQueries(queryClient, input.adapter);
  // Announced once accepted: a start the API refuses never promised a file.
  if (attachExportJob(key, jobId, Date.now())) {
    setExportJobNotice(key, showPreparing(job));
  }
  return { ok: true, key, jobId };
}

export interface SettleContext {
  queryClient: QueryClient;
  navigate: (href: string) => void;
}

/** The one place a job's terminal side effects run; its result takes over its toast. */
export async function settleExportJob(
  settlement: ExportJobSettlement,
  context: SettleContext,
): Promise<void> {
  try {
    await reportExportJob(settlement, context);
  } catch (error) {
    // E.g. a page's ready handler threw: never leave the toast spinning.
    const { noticeId } = settlement.job;
    if (noticeId !== null) showToast.dismiss(noticeId);
    throw error;
  }
}

async function reportExportJob(
  { job, verdict }: ExportJobSettlement,
  { queryClient, navigate }: SettleContext,
): Promise<void> {
  invalidateAdapterQueries(queryClient, job.adapter);

  switch (verdict.outcome) {
    case "ready":
      // Only a poll result is ever ready.
      return isPollingExportJob(job) ? deliverExport(job, verdict) : undefined;
    case "failed":
      return showExportFailedToast(job, verdict.error, navigate);
    case "lost":
      return showExportLostToast(job, navigate);
    case "timed_out":
      return showExportTimedOutToast(job, navigate);
  }
}

/** A job past the budget keeps polling: its toast says so, unless the user closed it. */
export function announceSlowExportJob(job: PollingExportJob): void {
  if (job.noticeId !== null) showPreparing(job);
}

/** Closing it forgets it: the job goes on, and its result shows as a new toast. */
function showPreparing(job: TrackedExportJob) {
  return showExportPreparingToast(job, () => setExportJobNotice(job.key, null));
}

async function deliverExport(
  job: PollingExportJob,
  { url, note }: { url: string; note?: string | null },
) {
  runExportReadyHandlers({
    scope: job.scope,
    jobId: job.jobId,
    url,
    label: job.label,
    format: job.format,
  });
  if (job.adapter.readyAction === "notify") {
    return showExportReadyToast(job, "notify", url, { note });
  }
  const saved = await downloadFile(url, job.filename ?? undefined);
  showExportReadyToast(job, saved ? "downloaded" : "undelivered", url, { note });
}

/**
 * The server announced a finished export (its bell entry): poll every tracked
 * job now, so it settles with that entry rather than up to a slow poll later.
 */
export function refreshExportJobs(queryClient: QueryClient): void {
  for (const job of exportJobsStore.getState().jobs) {
    if (!isPollingExportJob(job)) continue;
    void queryClient.invalidateQueries({
      queryKey: job.adapter.queryKey(job.jobId),
      exact: true,
    });
  }
}

/** Silent: org switch, logout, host unmount. */
export function cancelAllExportJobs(): void {
  for (const job of cancelExportJobs()) {
    if (job.noticeId !== null) showToast.dismiss(job.noticeId);
  }
}

function invalidateAdapterQueries(
  queryClient: QueryClient,
  adapter: ExportJobAdapter,
) {
  for (const queryKey of adapter.invalidates ?? []) {
    queryClient.invalidateQueries({ queryKey });
  }
}
